'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { COUNTRIES } from '@/utilities/countries'

export type CountrySelectProps = {
  value: string
  onChange: (code: string) => void
  name?: string
  id?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  placeholder?: string
  className?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

const LISTBOX_ID = 'country-listbox'

const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
)

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg className={`h-5 w-5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

export function CountrySelect({
  value, onChange, name = 'country', id, required, disabled, error,
  placeholder = 'Select country', className = '', 'aria-invalid': ariaInvalid, 'aria-describedby': ariaDescribedby,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selected = COUNTRIES.find((c) => c.code === value)
  const displayLabel = selected ? `${selected.name} (${selected.dialCode})` : placeholder

  const filteredCountries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return !q ? COUNTRIES : COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.code.toLowerCase().includes(q))
  }, [searchQuery])

  useEffect(() => {
    if (!open) { setSearchQuery(''); return }
    searchInputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const idx = value ? filteredCountries.findIndex((c) => c.code === value) : -1
    setHighlightIndex(idx >= 0 ? idx : -1)
  }, [open, filteredCountries, value])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEscape) }
  }, [open])

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true) }
  }

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex((i) => Math.min(i + 1, filteredCountries.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIndex((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      const c = filteredCountries[highlightIndex]
      if (c) { onChange(c.code); setOpen(false) }
    } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
  }

  const borderClass = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-black focus:border-[#E40520] focus:ring-[#E40520]'

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input type="hidden" name={name} value={value} autoComplete="off" />
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-haspopup="listbox"
        aria-invalid={ariaInvalid ?? error}
        aria-describedby={ariaDescribedby}
        aria-required={required}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        className={`flex w-full items-center justify-between rounded border bg-white px-4 py-3 font-poppins text-sm text-black placeholder:text-neutral-500 focus:outline-none focus:ring-1 disabled:opacity-60 ${borderClass} ${!selected ? 'text-neutral-500' : ''}`}
      >
        <span>{displayLabel}</span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-black bg-black shadow-lg">
          <div className="border-b border-white/20 px-3 py-2">
            <div className="flex items-center gap-2 rounded bg-white/10 px-3 py-2">
              <SearchIcon className="h-4 w-4 shrink-0 text-white" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleListKeyDown}
                placeholder="Search country..."
                className="w-full min-w-0 bg-transparent font-poppins text-sm text-white placeholder:text-white/60 focus:outline-none"
                autoComplete="off"
                aria-label="Search countries"
              />
            </div>
          </div>
          <ul
            id={LISTBOX_ID}
            role="listbox"
            aria-activedescendant={highlightIndex >= 0 ? `country-opt-${filteredCountries[highlightIndex]?.code}` : undefined}
            className="max-h-[240px] overflow-y-auto py-1"
          >
            {filteredCountries.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-white/60">No countries found</li>
            ) : (
              filteredCountries.map((c, i) => {
                const optId = `country-opt-${c.code}`
                const label = `${c.name} (${c.dialCode})`
                const isSelected = value === c.code
                const isHighlighted = i === highlightIndex
                return (
                  <li
                    key={c.code}
                    id={optId}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-sm text-white transition-colors ${isHighlighted ? 'bg-[#E40520]' : 'hover:bg-white/10'}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onChange(c.code)
                      setOpen(false)
                    }}
                    onMouseEnter={() => setHighlightIndex(i)}
                  >
                    <span>{label}</span>
                    {isSelected && (
                      <svg className="h-4 w-4 shrink-0 text-white" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
